import { useMemo } from "react";

import { DATE_FORMATS } from "@bps/date-time";
import {
  mergeStyles,
  ScrollablePane,
  SubmissionForm,
  SubmissionFormButtons,
  UseFormMethods
} from "@bps/fluent-ui";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  PremSettings,
  PremSettingsUpdate
} from "@libs/api/gateways/tg/tg-ops-gateway.dtos";
import { usePremSettingsMutation } from "@libs/api/gateways/tg/tg-ops-gateway.hooks";
import { guid } from "@libs/common/guid";
import { Permissions } from "@libs/permissions/permissions.enum";

import { PremSettingValueField } from "./prem-setting-value-field";
import { confirmPremSettings } from "./prem-settings-form.utils";

interface PremSettingsTableProps {
  premSettings: PremSettings[];
  siteId: guid;
}

type PremSettingsItem = PremSettings & { index: number };

export interface PremSettingFormValues {
  updateValues: PremSettingsUpdate[];
}

export const PremSettingsForm = ({
  premSettings,
  siteId
}: PremSettingsTableProps) => {
  const { data: hasWritePermissions } = usePermissionsCheckQuery(
    [Permissions.ProsSiteManagementWrite, Permissions.OpsConsolePreRelease],
    "or"
  );

  const items: PremSettingsItem[] = useMemo(
    () => premSettings.map((s, index) => ({ ...s, index })),
    [premSettings]
  );

  const { mutateAsync: updateSettings } = usePremSettingsMutation();

  const defaultValues: PremSettingFormValues = {
    updateValues: premSettings.map(p => ({
      settingName: p.settingName,
      settingValue: p.settingValue
    }))
  };

  const onConfirm = (changedValues: PremSettingsUpdate[]) => {
    return updateSettings({
      siteId,
      updates: changedValues
    });
  };

  const onSubmit = async (
    values: PremSettingFormValues,
    actions: Pick<UseFormMethods<PremSettingFormValues>, "reset" | "setValue">
  ) => {
    await confirmPremSettings({ premSettings, values, onConfirm });
    actions.reset(values);
  };

  const columns: DataTableColumn<PremSettings>[] = [
    {
      key: "settingName",
      name: "Setting Name",
      minWidth: 350,
      maxWidth: 400,
      sort: true,
      isSorted: true,
      onRender: (item: PremSettingsItem) => {
        return item.settingName;
      }
    },
    {
      key: "settingValue",
      name: "Setting Value",
      minWidth: 200,
      maxWidth: 700,
      onRender: (item: PremSettingsItem) => (
        <PremSettingValueField
          index={item.index}
          hasWritePermissions={!!hasWritePermissions}
        />
      )
    },
    {
      key: "created",
      name: "Created Date",
      minWidth: 200,
      sort: true,
      isSorted: true,
      onRender: (item: PremSettingsItem) =>
        item?.created && item?.created.isValid
          ? item.created.toFormat(
              DATE_FORMATS.LONG_DATE_TIME_FORMAT_MONTH_AHEAD
            )
          : "-"
    },
    {
      key: "updated",
      name: "Updated Date",
      minWidth: 200,
      sort: true,
      isSorted: true,
      onRender: (item: PremSettings) =>
        item?.updated && item?.updated.isValid
          ? item?.updated.toFormat(
              DATE_FORMATS.LONG_DATE_TIME_FORMAT_MONTH_AHEAD
            )
          : "-"
    }
  ];

  const getKey = (PremSettings: PremSettings) => PremSettings.settingName;
  return (
    <SubmissionForm<PremSettingFormValues>
      onSubmit={onSubmit}
      initialValues={defaultValues}
      styles={{ root: { flexGrow: 1 }, fields: { height: "100%" } }}
      hideButtons
    >
      {() => (
        <>
          {!!hasWritePermissions && (
            <SubmissionFormButtons
              cancelButtonProps={formApi => ({
                text: "Reset",
                onClick: () =>
                  formApi.actions.reset(formApi.state.initialValues)
              })}
              classNames={{
                buttonsWrapper: mergeStyles({
                  display: "flex",
                  marginBottom: 8,
                  width: "100%"
                }),
                buttons: mergeStyles({
                  marginLeft: "auto",
                  gap: 8,
                  display: "flex"
                })
              }}
            />
          )}
          <div style={{ position: "relative", height: "100%" }}>
            <ScrollablePane>
              <DataTable
                stickyHeader
                items={items}
                columns={columns}
                getKey={getKey}
              />
            </ScrollablePane>
          </div>
        </>
      )}
    </SubmissionForm>
  );
};
